import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  InputAdornment,
  Select,
  MenuItem,
} from "@mui/material";

import { z } from "zod";

import { FormControl } from "@ui";
import ThemeSelect from "@/ui/fields/Select";

import { femaCapture } from "@/shared/fema/capture";
import { sharePageEndpoints } from "@/shared/api/endpoints";
import {
  ApiResponse,
  CustomerDeclarationEntry,
  CustomerSharingResponse,
  DigitalLender,
  PaymentOfferUpsertRequest,
  SharedDocResponse,
} from "@/shared/api/types";
import { httpPost } from "@/shared/api/http";
import { LenderParentRecord } from "@/shared/api/services/lenderService";
import { ThemeColours } from "@/clients/acme/config/colours";
import { getCompanyDocs, ordinal } from "@/shared/share/sharePageUtils";
import { formatDate } from "@/shared/lib/utils/formatDate";
import { useAlert } from "../app/providers/AlertProvider";
import { UI_MESSAGES } from "../constants/ui";
import SpecialTextField from "@/ui/fields/SpecialTextField";
import { useNavigate } from "react-router-dom";
import { useReferenceIdHelp } from "@/components/referenceId";
import { DebtsService } from "@/shared/api/services";

type DeclarationStatusType = "not-completed" | "accepted" | "rejected";

export interface Company {
  id: string;
  name: string;
  reference: string;
  type: string;
  address: string;
  documents: any[];
  selectedDocuments: string[];
  lastShared: string;
  raw: any;
  isFp: boolean;
  declarationStatus?: DeclarationStatusType;
}

interface HttpErrorWithMessage {
  response?: {
    data?: {
      errorMessage?: string;
    };
  };
}

export interface AddCompanyFormProps {
  ieMstrLenderData: LenderParentRecord[];
  digitalLenders: DigitalLender[];
  setCompanies?: React.Dispatch<React.SetStateAction<Company[]>> | undefined;
  freepostCreditorNamesData: DigitalLender[];
  declarationData: CustomerDeclarationEntry[];
  setCompanyShareLastUpdateDate?:
    | React.Dispatch<React.SetStateAction<number>>
    | undefined;
  onBack: () => void;
  onSuccess?: () => void;
  source: string;
  companies?: Company[];
  balanceAmount?: number;
}

const DigitalLenderSchema = z
  .object({
    companyName: z
      .string()
      .min(1, "Invalid company selection")
      .refine((val) => val !== "I can't find my company", {
        message: "Invalid company selection",
      }),
    referenceId: z.string(),
    noReferenceId: z.boolean().optional().default(false),
    productName: z.string(),
  })
  .refine((data) => data.noReferenceId || data.referenceId.trim().length > 0, {
    message: "Reference ID is required",
    path: ["referenceId"],
  })
  .refine(
    (data) =>
      !data.companyName.toLowerCase().includes("barclay") ||
      data.productName.trim().length > 0,
    {
      message: "Product name is required",
      path: ["productName"],
    }
  );

const FreepostSchema = z
  .object({
    companyName: z.literal("I can't find my company"),
    noReferenceId: z.boolean().optional().default(false),
    referenceId: z.string(),
    freepostName: z.string().min(1, "Please select a freepost company"),
    address: z.string().min(1, "Address is required"),
    coveringNotes: z.string().min(1, "Covering notes are required"),
  })
  .refine((data) => data.noReferenceId || data.referenceId.trim().length > 0, {
    message: "Reference ID is required",
    path: ["referenceId"],
  });

function validateAddCompany(data: {
  companyName: string;
  referenceId: string;
  noReferenceId?: boolean;
  freepostName: string;
  address: string;
  coveringNotes: string;
  productName: string;
}) {
  if (data.companyName === "I can't find my company") {
    return FreepostSchema.safeParse(data);
  }
  return DigitalLenderSchema.safeParse(data);
}

function buildCompanyData(
  companyName: string,
  digitalLenders: DigitalLender[]
): {
  lender: string;
  schemaName: string;
  subDomain: string;
} {
  const found = digitalLenders.find(
    (item) => item.lender === companyName || item.creditor === companyName
  );
  if (!found) {
    return {
      lender: companyName,
      schemaName: "",
      subDomain: "",
    };
  }
  return {
    lender: found.lender,
    schemaName: found.schemaName,
    subDomain: found.subDomain,
  };
}

const AddCompanyForm: React.FC<AddCompanyFormProps> = ({
  ieMstrLenderData: _ieMstrLenderData,
  digitalLenders,
  setCompanies,
  freepostCreditorNamesData,
  declarationData,
  setCompanyShareLastUpdateDate,
  onBack,
  onSuccess,
  source,
  companies,
  balanceAmount,
}) => {
  const [digitalLenderEntry, setDigitalLenderEntry] = useState<DigitalLender>({
    schemaName: "",
    creditor: "",
    subDomain: "",
    lender: "",
    referenceNumberFormat: "",
    customerPaymentOffer: "",
    showCompanyList: "",
    addToDebtList: "",
    parentCompany: "",
    creditorAddress: "",
    isCreditorExistsWithIeHub: "",
  });

  const [paymentOfferAmount, setPaymentOfferAmount] = useState(null);
  const [paymentPreferredFrequency, setPaymentPreferredFrequency] =
    useState("");
  const [companyName, setCompanyName] = useState("");
  const [freepostName, setFreepostName] = useState("");
  const [referenceId, setReferenceId] = useState("");
  const navigate = useNavigate();
  const [noReferenceId, setNoReferenceId] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [address, setAddress] = useState("");
  const [coveringNotes, setCoveringNotes] = useState("");
  const [freepostCreditorNamesList, setFreepostCreditorNamesList] = useState<
    DigitalLender[]
  >([]);
  const [filteredCreditorNamesList, setFilteredCreditorNamesList] = useState<
    string[]
  >([]);
  const [selectionContext, setSelectionContext] = useState<
    Record<string, string[]>
  >({});

  const [
    filteredFreepostCreditorNamesList,
    setFilteredFreepostCreditorNamesList,
  ] = useState<string[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [productName, setProductName] = useState<string>("");
  const { showAlert } = useAlert();

  const selectedCompanyForHelp = useMemo(() => {
    if (companyName === "I can't find my company") {
      return freepostName || companyName;
    }
    return productName || companyName;
  }, [companyName, freepostName, productName]);

  const {
    helpDialog: referenceIdHelpDialog,
    helpLinkText: referenceIdHelpLinkText,
    noValueCheckboxLabel: addCompanyNoValueCheckboxLabel,
    label: addCompanyReferenceLabel,
  } = useReferenceIdHelp({ companyName: selectedCompanyForHelp });

  // GET Digital Creditor List Names
  useEffect(() => {
    let derivedSelectionContext: Record<string, string[]> = {};

    const filtered = digitalLenders
      .filter((item: DigitalLender) => {
        if (item.parentCompany) {
          derivedSelectionContext[item.parentCompany] = [
            ...(derivedSelectionContext[item.parentCompany] || []),
            item.creditor,
          ];
        }

        return (
          (!item.parentCompany || item.parentCompany === null) &&
          item.showCompanyList === "Y"
        );
      })
      .map((creditor: DigitalLender) => {
        return creditor.creditor;
      });

    setSelectionContext(derivedSelectionContext);
    setFilteredCreditorNamesList(filtered);
  }, [digitalLenders]);

  // SET corresponding digital lender entry
  useEffect(() => {
    const digitalLenderEntry = digitalLenders.find((c) => {
      return c.creditor.toLowerCase() == companyName.toLowerCase().trim();
    });

    if (!digitalLenderEntry) {
      setDigitalLenderEntry({
        schemaName: "",
        creditor: "",
        subDomain: "",
        lender: "",
        referenceNumberFormat: "",
        customerPaymentOffer: "",
        showCompanyList: "",
        addToDebtList: "",
        parentCompany: "",
        creditorAddress: "",
        isCreditorExistsWithIeHub: "",
      });
      return;
    }

    setDigitalLenderEntry(digitalLenderEntry);
  }, [companyName, digitalLenders]);

  // SET covering note
  useEffect(() => {
    const customerDataStr = localStorage.getItem("customerData");
    if (customerDataStr) {
      const customerData = JSON.parse(customerDataStr);
      if (customerData?.mobileNumber) {
        setCoveringNotes(
          `I am in financial difficulty and because of this I have used IE Hub to review my budget.  I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you. Please call me on ${customerData.mobileNumber} so we can discuss my options.`
        );
      } else {
        setCoveringNotes(
          `I am in financial difficulty and because of this I have used IE Hub to review my budget.  I have enclosed a copy for your record. You will be able to see from this information that I’m unable to make my full contractual payments to you.`
        );
      }
    }
  }, []);

  // GET Freepost Creditor List Names
  useEffect(() => {
    setFreepostCreditorNamesList(freepostCreditorNamesData);
    setFilteredFreepostCreditorNamesList(
      Array.from(
        new Set(
          freepostCreditorNamesData.map(
            (creditor: DigitalLender) => creditor.creditor
          )
        )
      )
    );
  }, [freepostCreditorNamesData]);

  const handleFreepostSelect = useCallback(
    (value: string) => {
      const company = freepostCreditorNamesList.find(
        (entry: DigitalLender) => entry.creditor === value
      );

      setFreepostName(value);

      if (company) {
        setAddress(company.creditorAddress);
      }
    },
    [freepostCreditorNamesList]
  );

  async function handleSave() {
    setIsSaving(true);

    const formData = {
      companyName: productName || companyName,
      referenceId,
      freepostName,
      address,
      noReferenceId,
      coveringNotes,
      productName,
    };

    const result = validateAddCompany(formData);

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      const formattedErrors: Record<string, string> = {};

      Object.keys(fieldErrors).forEach((key) => {
        const errorArray = fieldErrors[key as keyof typeof fieldErrors];
        if (errorArray && errorArray.length > 0 && errorArray[0]) {
          formattedErrors[key] = errorArray[0];
        }
      });

      setErrors(formattedErrors);
      setIsSaving(false);

      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    let newRefId = referenceId;

    if (noReferenceId) {
      newRefId = customerData.emailAddress;
    }

    if (newRefId.includes("'")) {
      showAlert({
        severity: "warning",
        message: "Please enter a valid reference number.",
        autoHide: true,
        duration: 2500,
      });
      setIsSaving(false);
      return;
    }

    const comparisonCompanyName =
      companyName !== "I can't find my company" ? companyName : freepostName;

    if (companies) {
      for (const company of companies) {
        if (
          company.reference == newRefId &&
          (comparisonCompanyName == company.name || productName == company.name)
        ) {
          showAlert({
            severity: "warning",
            message: `A company with the same reference number already exists`,
            autoHide: true,
            duration: 2000,
          });

          setIsSaving(false);

          return;
        }
      }
    }

    // Digital share
    if (companyName !== "I can't find my company") {
      const sharingCompany = buildCompanyData(
        formData.companyName,
        digitalLenders
      );

      // Create new share entry
      try {
        const payload = {
          accountNumber: newRefId,
          customerId: customerData.customerId,
          isSharing: "Y",
          lenderName: sharingCompany.lender,
          schemaName: sharingCompany.schemaName,
          subDomain: sharingCompany.subDomain,
          shareNow: "Y",
        };

        const response = await httpPost<CustomerSharingResponse>(
          sharePageEndpoints.createCustomerSharing(),
          payload
        );

        const companyDocsResponse = await getCompanyDocs(
          String(response.data.id),
          response.data.lenderName,
          response.data.accountNumber
        );

        const companyDocsArray = Array.isArray(companyDocsResponse)
          ? companyDocsResponse
          : (companyDocsResponse as ApiResponse<any[]>).data || [];

        const newDocs: Document[] = companyDocsArray.map(
          (doc: SharedDocResponse) => {
            return {
              id: String(doc.id),
              name: doc.documentName || doc.fileName,
              size: doc.documentSize,
              type: "wage-slip",
              uploadedDate: formatDate(doc.lastUpdateDate),
              raw: doc,
            };
          }
        );

        let selectedDocuments: string[] = [];

        for (const doc of newDocs) {
          if (doc?.raw?.isShared == "Y") {
            selectedDocuments.push(doc.id);
          }
        }

        const obj: Company = {
          id: String(response.data.id || newRefId),
          name: sharingCompany.lender,
          reference: newRefId,
          type: "all",
          address: "",
          documents: newDocs,
          selectedDocuments,
          lastShared: "",
          raw: response.data,
          isFp: false,
        };

        if (sharingCompany.lender === "United Utilities") {
          const companyDeclarationData = declarationData.find((entry) => {
            return entry.sharingId === response.data.id;
          });

          let declarationStatus: DeclarationStatusType =
            companyDeclarationData?.declarationStatus === "Accept"
              ? "accepted"
              : companyDeclarationData?.declarationStatus === "Reject"
                ? "rejected"
                : "not-completed";

          obj.declarationStatus = declarationStatus;
        }

        setCompanyShareLastUpdateDate &&
          setCompanyShareLastUpdateDate(
            new Date(response.data.lastUpdateDate).getTime()
          );

        source && source == "success" && navigate("/share");

        if (
          digitalLenderEntry.customerPaymentOffer == "Y" &&
          paymentOfferAmount &&
          Number(paymentOfferAmount) > 0
        ) {
          // Send payment offer
          const rawUserId = (customerData as { userId?: unknown } | null)
            ?.userId;
          const parsedUserId =
            typeof rawUserId === "number"
              ? rawUserId
              : typeof rawUserId === "string" && rawUserId.trim().length > 0
                ? Number(rawUserId)
                : null;
          const userIdForPayload =
            typeof parsedUserId === "number" && Number.isFinite(parsedUserId)
              ? parsedUserId
              : null;

          const poPayload: PaymentOfferUpsertRequest = {
            customerId: Number(customerData.customerId),
            lenderName: sharingCompany.lender,
            accountNumber: newRefId ?? null,
            schemaName: sharingCompany.schemaName ?? null,
            paymentOfferAmount: paymentOfferAmount
              ? Number(paymentOfferAmount)
              : null,
            paymentOfferMode: paymentPreferredFrequency ?? null,
            paymentPreferredFrequency: paymentPreferredFrequency ?? null,
            userId: userIdForPayload,
          };

          obj.paymentOfferData = {
            paymentOfferAmount: paymentOfferAmount
              ? Number(paymentOfferAmount)
              : null,
            paymentPreferredFrequency: ordinal(
              parseInt(paymentPreferredFrequency || "0")
            ),
          };

          await DebtsService.createPaymentOffer(poPayload, {
            skipErrorCapture: true,
          });
        }

        setCompanies && setCompanies((prev) => [...prev, obj]);

        if (onSuccess) {
          onSuccess();
        }
      } catch (error: unknown) {
        femaCapture("add_company_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });

        const errorMessage = (error as HttpErrorWithMessage).response?.data
          ?.errorMessage;

        if (
          typeof errorMessage === "string" &&
          errorMessage.includes("already exists")
        ) {
          showAlert({
            severity: "error",
            message:
              "This company with this same reference number already exists",
            autoHide: true,
            duration: 2500,
          });

          setIsSaving(false);

          return;
        }

        throw error;
      }
    } else {
      // Freepost share
      try {
        const payload = {
          companyName: freepostName,
          referenceNumber: newRefId,
          address: address,
          notes: coveringNotes,
          isTcAccepted: "Y",
          customerId: customerData.customerId,
          userId: customerData.userId,
          isApiCall: "Y",
          isSharing: "Y",
          shareNow: "N",
        };

        const response = await httpPost<CustomerSharingResponse>(
          sharePageEndpoints.createFreepostSharing(),
          payload
        );

        setCompanies &&
          setCompanies((prev) => [
            ...prev,
            {
              id: newRefId,
              name: freepostName,
              reference: newRefId,
              type: "all",
              address: address,
              documents: [],
              selectedDocuments: [],
              lastShared: "",
              raw: response.data,
              isFp: true,
            },
          ]);

        source && source == "success" && navigate("/share");

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        femaCapture("add_freepost_company_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }
  }

  return (
    <Box sx={{ pt: 3 }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Select the company you wish to share your budget information with.
      </Typography>

      {/* Company Name Dropdown */}
      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth error={!!errors.companyName}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, mb: 0.8, color: ThemeColours.label }}
          >
            Company name
          </Typography>
          <ThemeSelect
            dialogLabel="Select Company"
            options={[
              {
                value: "I can't find my company",
                label: "I can't find my company",
              },
              ...filteredCreditorNamesList.map((creditor) => ({
                value: creditor,
                label: creditor,
              })),
            ]}
            error={!!errors.companyName}
            validation={{
              required: true,
              requiredMessage: "Please select a valid company",
            }}
            value={companyName}
            onChange={(value) => setCompanyName(String(value))}
            placeholder="Select Company"
            showErrorBelow={false}
            searchable={true}
            fullWidth
            dataPrivate={false}
          />
        </FormControl>
      </Box>

      {selectionContext[companyName] && (
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth error={!!errors.productName}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, mb: 0.8, color: ThemeColours.label }}
            >
              Product Name
            </Typography>
            <ThemeSelect
              dialogLabel="Select a Product"
              options={[
                ...selectionContext[companyName].map((creditor) => ({
                  value: creditor,
                  label: creditor,
                })),
              ]}
              value={productName}
              onChange={(value) => setProductName(String(value))}
              placeholder="Select a Product"
              showErrorBelow={false}
              error={!!errors.productName}
              searchable={true}
              fullWidth
              dataPrivate={false}
            />
          </FormControl>
        </Box>
      )}

      {/* Company Name Freepost Dropdown */}
      {companyName === "I can't find my company" && (
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth error={!!errors.freepostName}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, mb: 0.8, color: ThemeColours.label }}
            >
              Company name (Choose one)
            </Typography>
            <ThemeSelect
              dialogLabel="Select a Company"
              options={[
                ...filteredFreepostCreditorNamesList.map((creditor) => ({
                  value: creditor,
                  label: creditor,
                })),
              ]}
              value={freepostName}
              onChange={(value) => handleFreepostSelect(String(value))}
              placeholder="Select one company"
              showErrorBelow={false}
              searchable={true}
              fullWidth
              dataPrivate={false}
            />
            {errors.freepostName && (
              <Typography color="error" variant="body2">
                {errors.freepostName}
              </Typography>
            )}
          </FormControl>
        </Box>
      )}

      <SpecialTextField
        isAccountField={true}
        noValueChecked={noReferenceId}
        label={addCompanyReferenceLabel}
        placeholder="Enter your account number"
        value={referenceId}
        onChange={(e) => setReferenceId(e.target.value)}
        onNoValueChange={(checked) => {
          setNoReferenceId(checked);
        }}
        error={!!errors.referenceId}
        helperText={errors.referenceId}
        allowNoValue={true}
        noValueCheckboxLabel={addCompanyNoValueCheckboxLabel}
        noValueFieldName="noReferenceId"
        helpLinkText={referenceIdHelpLinkText}
        helpDialog={referenceIdHelpDialog}
        showErrorBelow={true}
      />

      {(balanceAmount || 0) > 0 &&
        digitalLenderEntry.customerPaymentOffer == "Y" && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, mb: 0.8, color: ThemeColours.label }}
            >
              I would like to make a payment offer of
            </Typography>
            <TextField
              fullWidth
              value={paymentOfferAmount ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                  setPaymentOfferAmount(val === "" ? null : val);
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  maxHeight: 45,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">/month</InputAdornment>
                ),
                style: {
                  backgroundColor: "white",
                },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                mb: 0.8,
                color: ThemeColours.label,
                fontSize: 16,
              }}
            >
              Preferred payment day
            </Typography>
            <Select
              sx={{ backgroundColor: "#fff", mb: 1.5, maxHeight: 45 }}
              fullWidth
              value={paymentPreferredFrequency}
              onChange={(e) => setPaymentPreferredFrequency(e.target.value)}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

      {companyName === "I can't find my company" && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 2,
              mb: 0,
              mt: 2,
            }}
          >
            <Stack sx={{ width: "100%" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 0.5, fontSize: 26 }}
              >
                Freepost
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 400, mb: 0.8, lineHeight: 1.2 }}
              >
                The Company that you have asked to share with is not on our
                Online sharing list, but please don’t worry. We will print and
                post your budget at no cost to you and send it to them. If the
                address in the field below is not correct, please edit the
                address on this page, if the address field below is blank,
                please add the address you have from any correspondence from
                that company.
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  mb: 0.8,
                  color: ThemeColours.labelVar2,
                }}
              >
                Address
              </Typography>
              <TextField
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
                error={!!errors.address}
                helperText={errors.address}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.2,
                  },
                }}
                InputProps={{
                  style: {
                    backgroundColor: "white",
                  },
                }}
              />

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  mb: 0.8,
                  color: ThemeColours.labelVar2,
                }}
              >
                Covering Note
              </Typography>
              <TextField
                value={coveringNotes}
                onKeyDown={(e) => {
                  if (
                    coveringNotes.length >= 500 &&
                    e.key.length === 1 &&
                    !e.ctrlKey &&
                    !e.metaKey
                  ) {
                    showAlert({
                      severity: "warning",
                      message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                    });
                  }
                }}
                onPaste={(e) => {
                  const pastedText = e.clipboardData.getData("text");
                  const selectionStart = (e.target as any).selectionStart || 0;
                  const selectionEnd = (e.target as any).selectionEnd || 0;
                  const currentLength = coveringNotes.length;
                  if (
                    currentLength - (selectionEnd - selectionStart) + pastedText.length >
                    500
                  ) {
                    showAlert({
                      severity: "warning",
                      message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                    });
                  }
                }}
                onChange={(e) => setCoveringNotes(e.target.value)}
                fullWidth
                multiline
                rows={4}
                error={!!errors.coveringNotes}
                helperText={errors.coveringNotes}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.2,
                  },
                }}
                InputProps={{
                  style: {
                    backgroundColor: "white",
                  },
                }}
                inputProps={{ maxLength: 500 }}
              />
            </Stack>
          </Box>
        </>
      )}

      <Box
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Button
          onClick={onBack}
          sx={{
            height: "45px",
            backgroundColor: "#f3f4f6",
            borderRadius: "10px",
            color: "#1f2937",
            fontSize: "18px",
            fontWeight: 600,
            textTransform: "none",
            border: "none",
            "&:hover": {
              backgroundColor: "#e5e7eb",
              border: "none",
            },
            "&:disabled": {
              backgroundColor: "#f3f4f6",
              color: "#9ca3af",
            },
            minWidth: 100,
          }}
        >
          Back
        </Button>
        <Button
          onClick={handleSave}
          sx={{
            height: "45px",
            backgroundColor: "#D4E03B",
            borderRadius: "10px",
            color: "#003838",
            fontSize: "18px",
            fontWeight: 600,
            textTransform: "none",
            border: "none",
            "&:hover": {
              backgroundColor: "#e6f332",
              border: "none",
            },
            minWidth: 200,
          }}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default AddCompanyForm;
